/* eslint-disable no-await-in-loop */
import React, { useState } from 'react'
import { Document, pdf } from '@react-pdf/renderer'
import { PDFDocument } from 'pdf-lib'
import type { TaskCompletionDates, TemplateReportMonthlyData } from '@src/generatedClient/generated/taskreportsApi'
import { AxiosResponse } from 'axios'
import { ActivityGridPdf } from './ActivityGridPDF'
import dayjs from 'dayjs'

type Status = 'idle' | 'generating' | 'combining' | 'complete' | 'error'

type DataArg = {
  date: string
  data: AxiosResponse<TemplateReportMonthlyData>
}

interface ActivityGridReportGeneratorProps {
  fetchFunction: (arg: string) => Promise<DataArg>
  arguments: string[]
}

export function ActivityGridReportGenerator({
  fetchFunction,
  arguments: args,
}: ActivityGridReportGeneratorProps): JSX.Element {
  const [status, setStatus] = useState<Status>('idle')
  const [progress, setProgress] = useState<number>(0)

  const groupTaskCompletionsByMonth = (
    tasks: TaskCompletionDates[]
  ): Record<string, TaskCompletionDates[]> =>  tasks
      .slice()
      .sort((a, b) => a.taskName.localeCompare(b.taskName))
      .reduce((acc, task) => {
        const month = dayjs(task.startDate).local().format('YYYY-MM')
        if (!acc[month]) acc[month] = []
        acc[month].push(task)
        return acc
      }, {} as Record<string, TaskCompletionDates[]>)

  const generatePDFForData = async (
    month: string,
    data: TaskCompletionDates[],
  ): Promise<ArrayBuffer> => {
    function PageDocument() {
      return (
        <Document>
          <ActivityGridPdf month={month} completions={data} />
        </Document>
      )
    }

    try {
      const blob = await pdf(<PageDocument />).toBlob()
      console.log('Generated PDF blob size:', blob.size)
      return blob.arrayBuffer()
    } catch (error) {
      console.error('Error generating individual PDF:', error)
      throw error
    }
  }

  const generateAndCombinePDFs = async (): Promise<void> => {
    try {
      setStatus('generating')
      setProgress(0)
      const pdfDocs: ArrayBuffer[] = []

      let monthCompletions: Record<string, TaskCompletionDates[]> = {}

      const completions:  TaskCompletionDates[] = []

      const startDate = new Date(args[0]).toDateString()
      
      let daysPassed = 0
      let nextDate = dayjs(startDate).startOf('day')
      while (nextDate.isBefore(dayjs(args[args.length - 1]))) {
        const month = nextDate.local().format('YYYY-MM')
        if (!monthCompletions[month]) {
          monthCompletions[month] = []
        }

        const data = await fetchFunction(nextDate.toISOString())
        completions.push(...data.data.data.completionDates)
        nextDate = nextDate.add(31, 'day').startOf('day')
        daysPassed += 31
        if (daysPassed > args.length) {
          daysPassed = args.length
        }
        setProgress(Math.round((daysPassed / (args.length)) * 100))
      }

      monthCompletions = {
        ...monthCompletions,
        ...groupTaskCompletionsByMonth(completions),
      }

      const sortedMonths = Object.keys(monthCompletions)?.sort((a, b) => {
        const dateA = new Date(a)
        const dateB = new Date(b)
        return dateA.getTime() - dateB.getTime()
      }) ?? []

      for (const month of sortedMonths) {
        if (month) {
          const monthData = monthCompletions[month] ?? []

          const arrayBuffer = await generatePDFForData(month, monthData)
          console.log(`Generated PDF for ${month} size:`, arrayBuffer.byteLength)
          pdfDocs.push(arrayBuffer)
        }
      }

      setStatus('combining')
      console.log('Starting PDF combination')

      const mergedPdf = await PDFDocument.create()

      for (const pdfBytes of pdfDocs) {
        const p = await PDFDocument.load(pdfBytes)
        console.log('Loaded PDF page count:', p.getPageCount())
        const copiedPages = await mergedPdf.copyPages(p, p.getPageIndices())
        copiedPages.forEach((page) => mergedPdf.addPage(page))
      }

      const mergedPdfFile = await mergedPdf.save()
      console.log('Final merged PDF size:', mergedPdfFile.byteLength)

      const blob = new Blob([mergedPdfFile], { type: 'application/pdf' })
      const url = URL.createObjectURL(blob)

      const link = document.createElement('a')
      link.href = url
      link.download = 'combined.pdf'
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      URL.revokeObjectURL(url)

      setStatus('complete')
    } catch (error) {
      console.error('Error in PDF generation process:', error)
      setStatus('error')
    }
  }

  return (
    <div className="py-4">
      <button
        onClick={generateAndCombinePDFs}
        type="button"
        disabled={status === 'generating' || status === 'combining'}
        className="px-4 py-2 text-white bg-blue-500 rounded disabled:bg-gray-300"
      >
        {status === 'idle' && 'Activity Grid PDF'}
        {status === 'generating' && `Generating PDFs (${progress}%)`}
        {status === 'combining' && 'Combining PDFs...'}
        {status === 'complete' && 'Generation Complete'}
        {status === 'error' && 'Error Generating PDFs'}
      </button>

      {status !== 'idle' && (
        <div className="mt-4">
          <div className="h-2 bg-gray-200 rounded">
            <div
              className="h-full transition-all duration-300 bg-blue-500 rounded"
              style={{ width: `${progress}%` }}
            />
          </div>
        </div>
      )}
    </div>
  )
}
